import type { FC } from 'react'

import { ArrowUpRight } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { CONTACT_EMAIL, LINKS } from '@/constants/kinematic'

type FooterLink = {
    label: string
    href: string
    external?: boolean
}

type FooterColumn = {
    title: string
    links: FooterLink[]
}

const COLUMNS: FooterColumn[] = [
    {
        title: 'Product',
        links: [
            { label: 'Games', href: LINKS.lobby, external: true },
            { label: 'Theming', href: '#theming' },
            { label: 'Pricing', href: '#pricing' },
            { label: 'Dashboard', href: LINKS.signIn, external: true }
        ]
    },
    {
        title: 'Developers',
        links: [
            { label: 'Docs', href: LINKS.docs, external: true },
            { label: 'Quickstart', href: LINKS.quickstart, external: true },
            { label: 'Wallet API', href: LINKS.walletApi, external: true },
            { label: 'Provably fair', href: LINKS.provablyFair, external: true }
        ]
    },
    {
        title: 'Company',
        links: [
            { label: 'Contact', href: LINKS.email },
            { label: 'Terms', href: LINKS.legal, external: true },
            { label: 'Privacy', href: LINKS.legal, external: true }
        ]
    }
]

const SiteFooter: FC = () => {
    return (
        <footer className='border-t border-white/[0.06] bg-ink'>
            <div className='mx-auto grid max-w-[1320px] gap-12 px-5 py-16 md:px-8 lg:grid-cols-[1.5fr_repeat(3,1fr)]'>
                <div>
                    <Logo />

                    <p className='mt-5 max-w-xs text-sm leading-relaxed text-steel'>
                        Thirteen original casino games, served from your own game server on Cloudflare.
                    </p>

                    <a href={LINKS.email} className='mt-6 inline-flex font-mono text-xs text-bone underline decoration-white/20 underline-offset-4 transition-colors hover:decoration-ember'>
                        {CONTACT_EMAIL}
                    </a>
                </div>

                {COLUMNS.map(column => (
                    <nav key={column.title} aria-label={column.title}>
                        <h2 className='font-mono text-[11px] uppercase tracking-[0.18em] text-steel'>
                            {column.title}
                        </h2>

                        <ul className='mt-5 grid gap-3'>
                            {column.links.map(link => (
                                <li key={link.label}>
                                    <a
                                        href={link.href}
                                        {...(link.external ? { target: '_blank', rel: 'noreferrer' } : {})}
                                        className='group inline-flex items-center gap-1 text-sm text-bone/85 transition-colors hover:text-bone'
                                    >
                                        {link.label}
                                        {link.external && (
                                            <ArrowUpRight aria-hidden='true' className='size-3.5 text-steel transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                        )}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                ))}
            </div>

            <div className='border-t border-white/[0.06]'>
                <div className='mx-auto flex max-w-[1320px] flex-col gap-2 px-5 py-6 font-mono text-[11px] text-steel md:flex-row md:items-center md:justify-between md:px-8'>
                    <span>© 2026 HYPR PTE. LTD., Singapore</span>
                    <span>Demo games use test credits only.</span>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
